import { Area } from "./area.model";
import { Building, BuildingImage } from "./building.model";
import { DQStatus } from "./data-quality.model";
import { DtDto } from "./dt.model";
import { HistoryOutput } from "./history.model";
import { KeyValuePair } from "shared-lib";
import { BillingType, Meter, MeterCondition, MeterStatus } from "./meter.model";

export enum ServiceType {
  NotSelected = -1,
  Large = 0,
  Small = 1,
  Prepaid = 2,
  AMR = 3,
}

export enum AssetStatus {
  Pending = 0,
  Complete = 1,
  Incomplete = 2,
  Assigned = 'Assigned',
  Rejected = 4,
  Retagged = 5,
}

export enum CustomerType {
  Prepaid = 1,
  Postpaid = 2,
}

export enum BuildingType {
  NotSelected = 0,
  Residential = 1,
  Commercial = 2,
  Industrial = 3,
  Mixed = 4,
  ReligiousHouse = 5,
}

export class History {
  label: string;
  values: string[];

  constructor(data?: any) {
    this.label = data?.label ?? '';
    this.values = data?.values ?? [];
  }
}

export class SltStatus {
  id: number = 0;
  value: string = '';
  utilityId: number = 0;
  checked: boolean = false;
  name: string = '';
  installationAndPowerSource: InstallationAndPowerSource;
  friendlyInstallationAndPowerSource?: string = '';
}

export enum InstallationAndPowerSource {
  None = 0,
  High_Capacity = 1,
  Standard = 2
}

export interface CustomerCrudTable {
  id: number;
  region: string;
  area: string;
  feeder: string;
  dtName: string;
  dtNumber: string;
  buildingOwnerName: string;
  address: string;
  accountNumber: string;
  itinerary: string;
  meterNumber: string;
  tariffClass: string;
  customerType: string;
  contractNumber: string;
  blockNumber: number;
  geoCode: number;
  typeOfConnection: string;
  clientType: string;
  serviceType: string;
  activityCode: string;
  currentPlotCode: string;
  ghanaPostGPS: string;
  sltStatus: string;
  assetStatus?: string;
  postCode?: string;
  postCodeException?: string;
}

export enum ClientType {
  NotSelected = -1,
  Company = 0,
  EmbassyForeignInstitution = 1,
  Individual = 2,
  Government = 3,
  Assembly = 4,
}

export enum TypeOfConnection {
  NotSelected = -1,
  SinglePhase = 0,
  ThreePhase = 1,
}

export enum Neighborhood {
  Urban = 0,
  PeriUrban = 1,
  Rural = 2,
}

export interface CustomerAdd extends Customer {
  blockNumber: number | null;
  geoCode: number | null;
  activityCode: string;
  currentPlotCode: string;
  ghanaPostGPS: string;
  sltStatus: SltStatus | undefined;
  customerType: CustomerType | null;
  buildingType: BuildingType;
  regionId: number;
  clientType: ClientType;
  history: HistoryOutput;
  serviceType: ServiceType;
  areaId: number;
  tariffId: number;
  dtId: number;
  typeOfConnection: TypeOfConnection;
  meter: Meter;
  billingType: BillingType;
  dt: DtDto;
  isNewConnection: boolean;
  utilityId: number;
}

export class Customer {
  id: number;
  dataQualityId: number;
  slrn: string;
  name: string;
  accountNumber: string | null;
  phoneNumber?: string;
  email?: string;
  address: string;
  tariffName: string;
  isNew: boolean;
  meterNo?: string;
  dtName?: string;
  assetStatus?: AssetStatus;
  tenantName?: string;
  buildingOwner?: string;
  physicalAddress?: string;
  buildingId?: number;
  surveyStatus: string;
  oldMeterNumber?: string;
  area: Area;
  isOnBoard?: boolean;
  spn: string;
  dqStatus?: DQStatus;
  isChecked: boolean;
  isAudited: boolean;
  isReviewed: boolean;
  constructor(data?: any) {
    this.id = data?.id ?? 0;
    this.dataQualityId = data?.dataQualityId ?? 0;
    this.slrn = data?.slrn ?? '';
    this.name = data?.name ?? '';
    this.accountNumber = data?.accountNumber ?? '';
    this.phoneNumber = data?.phoneNumber ?? '';
    this.email = data?.email ?? '';
    this.address = data?.address ?? '';
    this.tariffName = data?.tariffName ?? '';
    this.isNew = data?.isNew ?? false;
    this.meterNo = data?.meterNo ?? '';
    this.dtName = data?.dtName ?? '';
    this.assetStatus = data?.assetStatus;
    this.buildingOwner = data?.buildingOwner ?? '';
    this.physicalAddress = data?.physicalAddress ?? '';
    this.buildingId = data?.buildingId;
    this.surveyStatus = data?.surveyStatus ?? '';
    this.oldMeterNumber = data?.oldMeterNumber ?? '';
    this.area = new Area(data?.area);
    this.isOnBoard = data?.isOnBoard ?? false;
    this.spn = data?.spn ?? '';
    this.dqStatus = data?.dqStatus;
    this.isChecked = data?.isChecked ?? false;
    this.isAudited = data?.isAudited ?? false;
    this.isReviewed = data?.isReviewed ?? false;
  }
}

export class CustomerEdit extends Customer {
  building: Building;
  history: HistoryOutput;
  dt: DtDto;
  buildingType: BuildingType;
  meter: Meter;
  billImageUrl: string;
  cardImageUrl: string;
  meterImageUrl: string;
  meterNumberImageUrl: string;
  utilitySupplyImageUrl: string;
  supplyFuseImageUrl: string;
  backCardViewImageUrl: string;
  newMeterPictureUrl: string;
  buildingImages: BuildingImage[];
  numberOfHouseholds?: number;
  contractNumber: string;
  typeOfConnection: TypeOfConnection;
  ghanaPostGps?: string;
  postCodeException?: string;
  buildingOwnerName: string;
  customerType: CustomerType;
  serviceType: ServiceType;
  sltStatus: SltStatus;
  clientType: ClientType;
  meterCondition: MeterCondition;
  meterStatus: MeterStatus;
  meterSlrn: string;
  cardNumber: string;
  closestLandmark: string;
  feederId: number;
  dtId: number;
  meterTypeId: number;
  meterNumber: string;
  remarksAndRecommendations?: string;
  lastUpdateTime?: string;
  validatedBy?: string;
  validatedDate?: string;
  auditedBy?: string;
  auditedDate?: string;
  auditedStatus?: string;
  reviewedBy?: string;
  reviewDate?: string;
  reviewStatus?: string;
  dataQualityStatus?: DQStatus;
  guid: string;
  customerHistories: History[];
  lastPaymentAmount: string;
  lastPaymentDate: string;
  lastVendDate?: string;
  lastVendAmount?: number;
  currentDebtBalance: string;
  permiseType?: string;
  permiseCategory?: string;
  activity?: string;
  subActivity?: string;
  neighborhood: Neighborhood;
  numberOfRooms: number;
  tariffClass: string;
  propertyCode: string;
  actualTariffClass: string;
  customerInspectionConclusion: string;
  comment: string;
  dataValidationReview: KeyValuePair[] = [];
  taggedDate?: string;
  firstCaptureUser?: string;
  lastUpdateUser?: string;
  frmMeterNumber?: string;
  installationDate?: string;
  replacementDate?: string;
  geoCode?: number;
  electricalAddress: string;


  constructor(data?: any) {
    super();
    this.id = data?.id ?? 0;
    this.dataQualityId = data?.dataQualityId ?? 0;
    this.slrn = data?.slrn ?? '';
    this.name = data?.name ?? '';
    this.accountNumber = data?.accountNumber ?? '';
    this.phoneNumber = data?.phoneNumber ?? '';
    this.email = data?.email ?? '';
    this.address = data?.address ?? '';
    this.tariffName = data?.tariffName ?? '';
    this.isNew = data?.isNew ?? false;
    this.meterNo = data?.meterNo ?? '';
    this.dtName = data?.dtName ?? '';
    this.propertyCode = data?.propertyCode ?? '';
    this.assetStatus = data?.assetStatus;
    this.buildingOwner = data?.buildingOwner ?? '';
    this.physicalAddress = data?.physicalAddress ?? '';
    this.buildingId = data?.buildingId ?? 0;
    this.surveyStatus = data?.surveyStatus ?? '';
    this.oldMeterNumber = data?.oldMeterNumber ?? '';
    this.area = new Area(data?.area);
    this.isOnBoard = data?.isOnBoard ?? false;
    this.spn = data?.spn ?? '';
    this.dqStatus = data?.dqStatus;
    this.isChecked = data?.isChecked ?? false;
    this.building = new Building(data?.building);
    this.history = new HistoryOutput(data?.history);
    this.dt = new DtDto(data?.dt);
    this.buildingType = data?.buildingType ?? BuildingType.NotSelected;
    this.meter = new Meter(data?.meter);
    this.billImageUrl = data?.billImageUrl ?? '';
    this.cardImageUrl = data?.cardImageUrl ?? '';
    this.meterImageUrl = data?.meterImageUrl ?? '';
    this.meterNumberImageUrl = data?.meterNumberImageUrl ?? '';
    this.utilitySupplyImageUrl = data?.utilitySupplyImageUrl ?? '';
    this.supplyFuseImageUrl = data?.supplyFuseImageUrl ?? '';
    this.backCardViewImageUrl = data?.backCardViewImageUrl ?? '';
    this.newMeterPictureUrl = data?.newMeterImageUrl ?? '';
    this.buildingImages = data?.buildingImages?.length
      ? data?.buildingImages.map((el: any) => new BuildingImage(el))
      : [];
    this.numberOfHouseholds = data?.numberOfHouseholds;
    this.contractNumber = data?.contractNumber ?? '';
    this.typeOfConnection =
      data?.typeOfConnection ?? TypeOfConnection.NotSelected;
    this.ghanaPostGps = data?.ghanaPostGps ?? '';
    this.postCodeException = data?.postCodeException ?? '';
    this.buildingOwnerName = data?.buildingOwnerName ?? '';
    this.customerType = data?.customerType ?? CustomerType.Prepaid;
    this.serviceType = data?.serviceType ?? ServiceType.AMR;
    this.sltStatus = data?.sltStatus ?? new SltStatus();
    this.clientType = data?.clientType ?? ClientType.NotSelected;
    this.meterCondition = data?.meterCondition ?? undefined;
    this.meterStatus = data?.meterStatus ?? undefined;
    this.meterSlrn = data?.meterSlrn ?? '';
    this.cardNumber = data?.cardNumber ?? '';
    this.closestLandmark = data?.closestLandmark ?? '';
    this.feederId = data?.feederId ?? 0;
    this.dtId = data?.dtId ?? 0;
    this.meterTypeId = data?.meterTypeId ?? 0;
    this.meterNumber = data?.meterNumber ?? '';
    this.remarksAndRecommendations = data?.remarksAndRecommendations ?? '';
    this.lastUpdateTime = data?.lastUpdateTime ?? '';
    this.validatedBy = data?.validatedBy ?? '';
    this.validatedDate = data?.validatedDate ?? '';
    this.auditedBy = data?.auditedBy ?? '';
    this.auditedDate = data?.auditedDate ?? '';
    this.auditedStatus = data?.auditedStatus ?? '';
    this.reviewedBy = data?.reviewedBy ?? '';
    this.reviewDate = data?.reviewDate ?? '';
    this.reviewStatus = data?.reviewStatus ?? '';
    this.dataQualityStatus = data?.dataQualityStatus;
    this.customerHistories = data?.customerHistories?.length
      ? data?.customerHistories.map((el: any) => new History(el))
      : [];
    this.lastPaymentAmount = data?.lastPaymentAmount ?? '';
    this.lastPaymentDate = data?.lastPaymentDate ?? '';
    this.lastVendDate = data?.lastVendDate ?? '';
    this.lastVendAmount = data?.lastVendAmount;
    this.currentDebtBalance = data?.currentDebtBalance ?? '';
    this.permiseType = data?.permiseType ?? '';
    this.permiseCategory = data?.permiseCategory ?? '';
    this.activity = data?.activity ?? '';
    this.subActivity = data?.subActivity ?? '';
    this.neighborhood = data?.neighborhood ?? Neighborhood.Urban;
    this.numberOfRooms = data?.numberOfRooms ?? 0;
    this.tariffClass = data?.tariffClass ?? '';
    this.actualTariffClass = data?.actualTariffClass ?? '';
    this.customerInspectionConclusion =
      data?.customerInspectionConclusion ?? '';
    this.tenantName = data?.tenantName ?? '';
    this.comment = data?.comment ?? '';
    this.guid = data?.guid ?? '';
    this.dataValidationReview = data?.dataValidationReview ?? [];
    this.taggedDate = data?.taggedDate ?? '';
    this.firstCaptureUser = data?.firstCaptureUser ?? '';
    this.lastUpdateUser = data?.lastUpdateUser ?? '';
    this.frmMeterNumber = data?.frmMeterNumber ?? '';
    this.installationDate = data?.installationDate ?? '';
    this.replacementDate = data?.replacementDate ?? '';
    this.geoCode = data?.geoCode;
    this.electricalAddress = data?.electricalAddress ?? '';
  }
}

export enum PhoneNumberVerificationIssue {
  Inactive = 1,
  Mismatch = 2,
  DeniedOwnership = 3,
  Invalid = 4
}

export enum AuditFailedReasons {
  Email = 1,
  CardNumber = 2,
  SlrnMismatch = 3,
  TagNotFound = 4,
  BuildingStructure = 5,
  NoMeterNo = 6,
  SlrnIncorrect = 7,
  Inaccessible = 8,
  DevelopmentStage = 9,
  Other = 10
}

export class CustomerCrudFilter {
  search: string | null = null;
  isNewConnection: boolean = false;
  tariffId: number | null = null;
  serviceType: ServiceType | null = null;
  customerStatus: number | null = null;
  clientType: ClientType | null = null;
  sltStatus: number | null = null;
  customerPhase: TypeOfConnection | null = null;
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];
}

export class CustomerCrudAppliedFilter {
  id?: number;
  label: string = '';
  value: any = '';
}